import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useNavigate } from 'react-router-dom';
import {
  listMealPlanTemplates,
  type MealPlanTemplateKind,
  type MealPlanTemplateSummary,
} from '@/lib/api/mealPlans';
import { extractErrorMessage } from '@/utils/toast-utils';
import { MealPlanRestrictionBadges } from '@/components/shared/MealPlanRestrictionBadges';
import {
  buildMealPlanRestrictionWarning,
  type ClientRestrictionProfile,
} from '@/utils/meal-plan-allergen-warnings';
import { Tag } from '@/components/ui/tag';
import { formatUnderscoreDisplayLabel } from '@/utils/display-labels';

const ACCENT = '#20AEDC';
const PAGE_SIZE = 100;
const DEFAULT_SEARCH_PLACEHOLDER = 'Search templates…';

type TemplatesTab = 'meal_plan' | 'meal_slot';

const normalizeKind = (templateKind?: MealPlanTemplateKind): MealPlanTemplateKind =>
  templateKind === 'meal_slot' ? 'meal_slot' : 'meal_plan';

const getTemplateTitle = (template: MealPlanTemplateSummary): string =>
  template.templateName?.trim() || template.name?.trim() || 'Untitled template';

function TabButton(props: {
  label: string;
  count: number;
  selected: boolean;
  onClick: () => void;
}) {
  const { label, count, selected, onClick } = props;
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        border: 'none',
        bgcolor: 'transparent',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        minWidth: 0,
        p: 0,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.625} sx={{ px: 2, py: '11px' }}>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '14px',
            lineHeight: '20px',
            fontWeight: selected ? 500 : 400,
            color: selected ? ACCENT : '#717182',
            textAlign: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
        <Box
          sx={{
            bgcolor: selected ? ACCENT : '#ececf0',
            borderRadius: '999px',
            px: 1,
            py: '2px',
            minHeight: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '12px',
              lineHeight: '16px',
              fontWeight: selected ? 700 : 400,
              color: selected ? '#fff' : '#717182',
            }}
          >
            {count}
          </Typography>
        </Box>
      </Stack>
      <Box
        sx={{
          height: '2px',
          bgcolor: selected ? ACCENT : 'transparent',
          borderRadius: '1px',
          mx: '8px',
        }}
      />
    </Box>
  );
}

function TemplateResultRow({
  template,
  onSelect,
  selected,
  showImportCue,
}: {
  template: MealPlanTemplateSummary;
  onSelect: () => void;
  selected: boolean;
  showImportCue?: boolean;
}) {
  const title = getTemplateTitle(template);
  const suitable = template.suitableFor?.trim() || '';
  const clinical = Array.from(
    new Map(
      [...(template.clinicalTags ?? []), ...(template.tags ?? [])]
        .map((t) => t.trim())
        .filter(Boolean)
        .map((t) => [t.toLowerCase(), t] as const),
    ).values(),
  );
  const primaryTag = clinical[0];
  const secondaryTags = clinical.slice(1);

  const p = typeof template.protein === 'number' && Number.isFinite(template.protein)
    ? template.protein.toFixed(1)
    : '0.0';
  const c = typeof template.carbs === 'number' && Number.isFinite(template.carbs)
    ? template.carbs.toFixed(1)
    : '0.0';
  const f = typeof template.fat === 'number' && Number.isFinite(template.fat)
    ? template.fat.toFixed(1)
    : '0.0';
  const kcal =
    typeof template.calories === 'number' && Number.isFinite(template.calories)
      ? Math.round(template.calories)
      : '—';

  return (
    <Box
      component="button"
      type="button"
      onClick={onSelect}
      aria-label={
        showImportCue
          ? `Select template to import: ${title}`
          : `Select template: ${title}`
      }
      sx={{
        width: '100%',
        border: 'none',
        bgcolor: selected ? '#ecfeff' : 'transparent',
        cursor: 'pointer',
        textAlign: 'left',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        px: '20px',
        py: '12px',
        '&:hover': { bgcolor: selected ? '#ecfeff' : '#fafafa' },
      }}
    >
      <Stack spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, minWidth: 0 }}>
          <Typography
            sx={{
              minWidth: 0,
              maxWidth: '100%',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '14px',
              lineHeight: '17.5px',
              color: '#1e2939',
              letterSpacing: '-0.1504px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {title}
          </Typography>
          <MealPlanRestrictionBadges source={template} />
        </Box>
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.75}>
          {suitable ? (
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                fontSize: '12px',
                lineHeight: '18px',
                color: '#9ca3af',
              }}
            >
              {suitable}
            </Typography>
          ) : null}
          {primaryTag ? (
            <Tag
              variant="default"
              size="sm"
              className="bg-[#E6FAF8] px-2 py-[2.5px] font-[Inter,sans-serif] font-semibold leading-[16.5px] text-[#0D9488]"
            >
              {formatUnderscoreDisplayLabel(primaryTag)}
            </Tag>
          ) : null}
          {secondaryTags.map((tag) => (
            <Tag
              key={tag}
              variant="neutral"
              size="sm"
              className="bg-[#F3F4F6] px-2 py-[2.5px] font-[Inter,sans-serif] font-medium leading-[16.5px] text-[#6B7280]"
            >
              {formatUnderscoreDisplayLabel(tag)}
            </Tag>
          ))}
        </Stack>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 500,
            fontSize: '12px',
            lineHeight: '16px',
            color: '#9ca3af',
          }}
        >
          {`P:${p}g C:${c}g F:${f}g`}
        </Typography>
      </Stack>
      <Stack alignItems="flex-end" spacing={0.35} sx={{ flexShrink: 0 }}>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: '14px',
            lineHeight: '21px',
            color: ACCENT,
            letterSpacing: '-0.1504px',
          }}
        >
          {`${kcal} kcal`}
        </Typography>
        {/* selection state is shown by row highlight; no extra badges under kcal */}
      </Stack>
    </Box>
  );
}

export type TemplatesSearchPopoverFieldProps = {
  /** Trigger label; prefixed with "+ " if missing. Shown like meal card "+ Import from Recipes". */
  placeholder: string;
  /** Placeholder for the search field inside the dialog. */
  searchPlaceholder?: string;
  onTemplatePick?: (template: MealPlanTemplateSummary) => void | Promise<void>;
  /**
   * When true (e.g. picking runs import into the current day), list rows show import cue — not navigation.
   */
  importOnSelect?: boolean;
  /** Hide templates already recorded on the meal plan as `importedTemplates` (re-import not offered). */
  excludeImportedTemplateIds?: string[];
  /** Client restrictions used to show an in-dialog warning before importing. */
  restrictionClient?: ClientRestrictionProfile;
};

const TemplatesSearchPopoverField: React.FC<TemplatesSearchPopoverFieldProps> = ({
  placeholder,
  searchPlaceholder = DEFAULT_SEARCH_PLACEHOLDER,
  onTemplatePick,
  importOnSelect = false,
  excludeImportedTemplateIds,
  restrictionClient,
}) => {
  const navigate = useNavigate();
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [debouncedQuery, setDebouncedQuery] = React.useState('');
  const [tab, setTab] = React.useState<TemplatesTab>('meal_plan');
  const [templates, setTemplates] = React.useState<MealPlanTemplateSummary[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [fetchError, setFetchError] = React.useState('');
  const [selectedTemplateId, setSelectedTemplateId] = React.useState<string | null>(null);

  React.useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 300);
    return () => window.clearTimeout(t);
  }, [query]);

  React.useEffect(() => {
    if (!open) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setFetchError('');
      try {
        const [mealPlansRes, mealSlotsRes] = await Promise.all([
          listMealPlanTemplates({
            page: 1,
            pageSize: PAGE_SIZE,
            search: debouncedQuery || undefined,
            templateKind: 'meal_plan',
          }),
          listMealPlanTemplates({
            page: 1,
            pageSize: PAGE_SIZE,
            search: debouncedQuery || undefined,
            templateKind: 'meal_slot',
          }),
        ]);
        if (cancelled) return;
        if (!mealPlansRes.success) {
          throw new Error(mealPlansRes.message || 'Failed to load meal plan templates');
        }
        if (!mealSlotsRes.success) {
          throw new Error(mealSlotsRes.message || 'Failed to load meal slot templates');
        }
        setTemplates([
          ...(mealPlansRes.data?.templates ?? []),
          ...(mealSlotsRes.data?.templates ?? []),
        ]);
      } catch (err) {
        if (!cancelled) {
          setTemplates([]);
          setFetchError(extractErrorMessage(err) || 'Failed to load templates');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [open, debouncedQuery]);

  React.useEffect(() => {
    if (!open) return;
    const id = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  const hiddenTemplateIdSet = React.useMemo(() => {
    return new Set(
      (excludeImportedTemplateIds ?? [])
        .map((id) => String(id).trim())
        .filter(Boolean),
    );
  }, [excludeImportedTemplateIds]);

  const partitioned = React.useMemo(() => {
    const mealPlansAll: MealPlanTemplateSummary[] = [];
    const mealSlots: MealPlanTemplateSummary[] = [];

    for (const t of templates) {
      if (importOnSelect && hiddenTemplateIdSet.has(String(t.id).trim())) continue;
      const kind = normalizeKind(t.templateKind);
      if (kind === 'meal_slot') {
        mealSlots.push(t);
      } else {
        mealPlansAll.push(t);
      }
    }

    return {
      meal_plan: mealPlansAll,
      meal_slot: mealSlots,
    };
  }, [templates, hiddenTemplateIdSet, importOnSelect]);

  const counts = React.useMemo(
    () => ({
      meal_plan: partitioned.meal_plan.length,
      meal_slot: partitioned.meal_slot.length,
    }),
    [partitioned],
  );

  const visibleList = partitioned[tab];
  const selectedTemplate = React.useMemo(
    () => templates.find((template) => template.id === selectedTemplateId) ?? null,
    [selectedTemplateId, templates],
  );
  const selectedRestrictionWarning = React.useMemo(
    () => (
      importOnSelect && selectedTemplate
        ? buildMealPlanRestrictionWarning(selectedTemplate, restrictionClient, 'template')
        : null
    ),
    [importOnSelect, restrictionClient, selectedTemplate],
  );

  const handleNavigate = (id: string) => {
    navigate(`/templates/${encodeURIComponent(id)}/view`);
    setOpen(false);
  };

  const handleApplyTemplate = async () => {
    if (!selectedTemplate) return;
    try {
      if (onTemplatePick) {
        await onTemplatePick(selectedTemplate);
        setOpen(false);
        return;
      }
      handleNavigate(selectedTemplate.id);
    } catch (err) {
      setFetchError(extractErrorMessage(err) || 'Unable to apply template');
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setTab('meal_plan');
    setQuery('');
    setDebouncedQuery('');
    setFetchError('');
    setSelectedTemplateId(null);
  };

  const handleClose = () => {
    setOpen(false);
  };

  React.useEffect(() => {
    if (!open) return;
    setSelectedTemplateId(null);
  }, [open, tab, debouncedQuery]);

  /** Match `NutritionalAnalysisMealCard` "+ Import from Recipes" control (outlined, compact). */
  const triggerLabel = React.useMemo(() => {
    const t = placeholder.trim();
    if (!t) return '+ Import from Templates';
    return t.startsWith('+') ? t : `+ ${t}`;
  }, [placeholder]);

  return (
    <>
      <Button
        type="button"
        variant="outlined"
        onClick={handleOpen}
        sx={{
          textTransform: 'none',
          fontSize: '0.8rem',
          minHeight: 34,
          px: 1.3,
          borderColor: '#e4e7ec',
          color: '#667085',
        }}
      >
        {triggerLabel}
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: '16px',
              border: '1px solid #f3f4f6',
            },
          },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              px: '24px',
              pt: '20px',
              pb: '16px',
              borderBottom: '1px solid #F3F4F6',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: '12px',
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minHeight: '42.5px' }}>
              <Box
                sx={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '14px',
                  bgcolor: '#FFF7ED',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                <DownloadRoundedIcon sx={{ fontSize: 18, color: '#F97316' }} />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '18px',
                    fontWeight: 700,
                    lineHeight: '22.5px',
                    letterSpacing: '-0.4395px',
                    color: '#1E2939',
                  }}
                >
                  {importOnSelect ? 'Import from Templates' : 'Choose Template'}
                </Typography>
                {!importOnSelect ? (
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '12px',
                      fontWeight: 400,
                      lineHeight: '18px',
                      color: '#1EA9D6',
                    }}
                  >
                    Select one template to continue
                  </Typography>
                ) : null}
              </Box>
            </Stack>

            <IconButton
              type="button"
              onClick={handleClose}
              aria-label="Close"
              sx={{
                width: '32px',
                height: '32px',
                border: '1px solid #E5E7EB',
                borderRadius: '999px',
                color: '#9CA3AF',
                p: '1px',
                alignSelf: 'flex-start',
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 1.5, px: 2, pb: 2 }}>
          <TextField
            inputRef={searchInputRef}
            size="small"
            fullWidth
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                fontSize: '0.875rem',
                fontFamily: 'Inter, sans-serif',
                '& fieldset': { borderColor: '#e3e8ef' },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ color: '#98a2b3', fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ height: '1px', bgcolor: '#f3f4f6', mb: 0.5 }} />

          <Stack direction="row" alignItems="stretch" sx={{ mx: -2 }}>
            <TabButton
              label="Meal Plan"
              count={counts.meal_plan}
              selected={tab === 'meal_plan'}
              onClick={() => setTab('meal_plan')}
            />
            <TabButton
              label="Meal Slots"
              count={counts.meal_slot}
              selected={tab === 'meal_slot'}
              onClick={() => setTab('meal_slot')}
            />
          </Stack>

          <Box
            sx={{
              maxHeight: { xs: '50vh', sm: 386 },
              overflowY: 'auto',
              mx: -2,
              borderTop: '1px solid #f3f4f6',
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} sx={{ color: ACCENT }} />
              </Box>
            ) : fetchError ? (
              <Typography sx={{ px: 2, py: 3, color: '#b42318', fontSize: '13px', fontFamily: 'Inter, sans-serif' }}>
                {fetchError}
              </Typography>
            ) : visibleList.length === 0 ? (
              <Typography sx={{ px: 2, py: 3, color: '#6b7280', fontSize: '13px', fontFamily: 'Inter, sans-serif' }}>
                No templates match this search.
              </Typography>
            ) : (
              visibleList.map((template) => (
                <TemplateResultRow
                  key={template.id}
                  template={template}
                  selected={selectedTemplateId === template.id}
                  showImportCue={importOnSelect}
                  onSelect={() => setSelectedTemplateId(template.id)}
                />
              ))
            )}
          </Box>
          {selectedRestrictionWarning ? (
            <Box
              sx={{
                mt: 1.25,
                border: '1px solid #FED7AA',
                bgcolor: '#FFF7ED',
                borderRadius: '12px',
                px: 1.5,
                py: 1.1,
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '12px',
                  lineHeight: '18px',
                  fontWeight: 700,
                  color: '#9A3412',
                  mb: 0.25,
                }}
              >
                Check client restrictions
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '12px',
                  lineHeight: '18px',
                  color: '#9A3412',
                }}
              >
                {selectedRestrictionWarning.message}
              </Typography>
            </Box>
          ) : null}
          <DialogActions
            sx={{
              px: 0,
              pt: 1.25,
              mt: 1,
              borderTop: '1px solid #F3F4F6',
            }}
          >
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{
                textTransform: 'none',
                borderRadius: '14px',
                height: '37.5px',
                minWidth: '85.391px',
                px: 2.2,
                color: '#374151',
                borderColor: '#E5E7EB',
                fontSize: '13px',
                fontWeight: 600,
                lineHeight: '19.5px',
                letterSpacing: '-0.0762px',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                void handleApplyTemplate();
              }}
              variant="contained"
              disabled={!selectedTemplateId}
              startIcon={importOnSelect ? <DownloadRoundedIcon sx={{ fontSize: 14, mr: 0.1 }} /> : undefined}
              sx={{
                textTransform: 'none',
                borderRadius: '14px',
                height: '35.5px',
                minWidth: 0,
                px: 2.2,
                bgcolor: selectedTemplateId ? '#1D2939' : '#9CA3AF',
                color: '#ffffff',
                fontSize: '13px',
                fontWeight: 600,
                lineHeight: '19.5px',
                letterSpacing: '-0.0762px',
                '&.Mui-disabled': { bgcolor: '#9CA3AF', color: '#fff' },
                '&:hover': { bgcolor: selectedTemplateId ? '#101828' : '#7f8ea4' },
              }}
            >
              {importOnSelect ? 'Import' : 'Open'}
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TemplatesSearchPopoverField;
